import { useEffect, useState, useSyncExternalStore } from 'react';
import { merge } from 'rxjs';
import {
    replicationStates,
    isReplicatingHere,
    subscribeReplicationPresence
} from '../../db/replication';
import {
    pendingLocalChanges,
    subscribeAuthorJournal
} from '../../db/authorJournal';
import { subscribeSyncBlockers, getSyncBlockers } from '../../db/syncBlockers';
import { isSyncFailing, subscribeSyncFailing } from './syncErrorLog';

export interface SyncStatus {
    pending: number;
    active: boolean;
    hasError: boolean;
    /** Names of colleagues whose queued changes await their own sign-in. */
    blockedBy: string[];
    /**
     * Whether THIS window is the one that syncs. Several windows share one
     * database and only one of them replicates (see db/replication); the others
     * are not broken, they are simply not the ones doing it.
     */
    replicatingHere: boolean;
}

export function useSyncStatus(): SyncStatus {
    const [status, setStatus] = useState<
        Omit<SyncStatus, 'replicatingHere' | 'hasError'>
    >({
        pending: 0,
        active: false,
        blockedBy: []
    });

    const replicatingHere = useSyncExternalStore(
        subscribeReplicationPresence,
        isReplicatingHere
    );

    /**
     * Whether the sync is broken is a fact about the DEVICE, not about this
     * window: `error$` and `sent$` fire only where the replication runs, so
     * reading them here left every other window green through a failure. It is
     * written at those two events (db/replication) into a store all windows
     * share.
     */
    const hasError = useSyncExternalStore(subscribeSyncFailing, isSyncFailing);

    /**
     * A held queue is a normal, actionable state, not a failure — the push
     * throws on purpose to keep those documents queued, and that throw would
     * otherwise flash the generic sync error every retry.
     */
    useEffect(
        () =>
            subscribeSyncBlockers(() =>
                setStatus((previous) => ({
                    ...previous,
                    blockedBy: getSyncBlockers().map((blocker) => blocker.name)
                }))
            ),
        []
    );

    /**
     * What is still waiting, read from the author journal rather than counted
     * in this window.
     *
     * The old counter went up on every local write and down on every `sent$`.
     * Both halves are per-window: in the window that does NOT replicate, `sent$`
     * never fires, so its badge climbed and stayed — the documents had long gone
     * up, pushed by the other window. It also started at zero after every
     * reload, which quietly reported an unsynced queue as "synchronisiert".
     *
     * The journal has neither problem: it holds one entry per document from the
     * write until the push is accepted (see db/authorJournal), it is shared by
     * every window of this browser, and it outlives a reload.
     */
    useEffect(() => {
        const update = () =>
            setStatus((previous) => ({
                ...previous,
                pending: pendingLocalChanges()
            }));
        update();
        return subscribeAuthorJournal(update);
    }, []);

    useEffect(() => {
        if (replicationStates.length === 0) {
            return;
        }

        const subscriptions = [
            // Track active state across all replications
            merge(
                ...replicationStates.map((replication) => replication.active$)
            ).subscribe((isActive) => {
                setStatus((previous) => ({ ...previous, active: isActive }));
            })
        ];

        return () => {
            subscriptions.forEach((subscription) => subscription.unsubscribe());
        };
    }, []);

    return { ...status, hasError, replicatingHere };
}
